import styled from 'styled-components';

import { BigParagraph, BigSubtitle, style } from '@genially/design-system';
import {
  GeniallyIconWhiteIcon,
  InfoFilledIcon,
  LockIcon,
  LogoutIcon,
  UnlockIcon,
} from '@genially/icons';

import { HostGameBreakdownSteps } from '../../HostGame.constants';

export const LeftSide = styled.div(({ theme: { size, transition } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: size.units(1),
  transition: transition.standard(),

  '& > *': {
    pointerEvents: 'auto',
  },
}));

export const StyledGeniallyIcon = styled(GeniallyIconWhiteIcon)(
  ({ theme: { color } }) => ({
    color: color.content.reversed.default(),
  }),
);

export const PlayLinkText = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const PlayIconAndLinkWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: size.units(1),
}));

export const SessionCodeText = styled(BigParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  cursor: 'pointer',
}));

export const BlockedText = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const SessionCode = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const StyledUnlockIcon = styled(UnlockIcon)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  cursor: 'pointer',
}));

export const IconWrapper = styled.div(({ theme: { size } }) => ({
  width: size.units(5),
  height: size.units(5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledLockIcon = styled(LockIcon)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  cursor: 'pointer',
}));

export const StyledInfoFilledIcon = styled(InfoFilledIcon)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  cursor: 'pointer',
}));

export const CentralSide = styled.div(({ theme: { size, transition } }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: size.units(3),
  transition: transition.standard(),

  '& > *': {
    pointerEvents: 'auto',
  },

  ...style.mixins.responsive.custom(`${HostGameBreakdownSteps.forth}px`).downward({
    paddingRight: size.units(2),
  }),
}));

export const RightSide = styled.div(({ theme: { size, transition } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: size.units(4),
  transition: transition.standard(),

  '& > *': {
    pointerEvents: 'auto',
  },
}));

export const CentralSideWrapper = styled.div<{ $isClickable: boolean }>(
  ({ theme: { borderRadius, color, size, transition }, $isClickable }) => ({
    display: 'flex',
    gap: size.units(3),
    alignItems: 'center',
    cursor: $isClickable ? 'pointer' : 'default',
    transition: transition.standard(),
    paddingLeft: size.units(1.5),
    borderBottomLeftRadius: borderRadius.large,
    borderBottomRightRadius: borderRadius.large,
    transformOrigin: 'top center',
    pointerEvents: 'auto',

    backgroundColor: color.background.reversed.default(0.5),

    ...($isClickable && {
      ':hover': {
        transform: 'scale(0.95)',
      },
    }),
  }),
);

interface WrapperProps {
  $isVisible?: boolean;
}

export const Wrapper = styled.div<WrapperProps>(({ theme: { size }, $isVisible }) => ({
  width: '100%',
  height: size.units(7),

  padding: `${size.units(1)} ${size.units(2)}`,

  display: 'flex',
  gap: size.units(1),
  overflow: 'hidden',
  pointerEvents: 'none',

  [CentralSide]: {
    flexGrow: 1,
  },

  [LeftSide]: {
    transform: $isVisible ? 'translateX(0)' : 'translateX(-150%)',
    opacity: $isVisible ? 1 : 0,
  },

  [CentralSideWrapper]: {
    marginTop: size.units(-2),
    transform: $isVisible ? 'translateY(0)' : 'translateY(-150%)',
    opacity: $isVisible ? 1 : 0,
  },

  [RightSide]: {
    transform: $isVisible ? 'translateX(0)' : 'translateX(150%)',
    opacity: $isVisible ? 1 : 0,
  },
}));

export const CloseSessionIcon = styled(LogoutIcon)(({ theme: { color, size } }) => ({
  width: size.units(2),
  height: size.units(2),
  minWidth: size.units(2),
  minHeight: size.units(2),
  color: color.content.reversed.default(),
}));
